import type { SerializedInventoryLocation } from '@enmon/commons/lib/tools';
import { type AppSettingsRepository, appSettingsRepository } from '@/app/settings/app-settings';
import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';

type ValidateLocationApiKeyResponse = Pick<SerializedInventoryLocation, 'id'>;

export class LocationApiKeyValidator {
  private readonly axiosClient?: InventoryAxiosClient;
  private readonly createAxiosClient: (input: { baseUrl?: string }) => InventoryAxiosClient;
  private readonly settingsRepository: AppSettingsRepository;

  constructor(
    input: {
      axiosClient?: InventoryAxiosClient;
      createAxiosClient?: (input: { baseUrl?: string }) => InventoryAxiosClient;
      settingsRepository?: AppSettingsRepository;
    } = {},
  ) {
    this.axiosClient = input.axiosClient;
    this.createAxiosClient = input.createAxiosClient ?? createInventoryAxiosClient;
    this.settingsRepository = input.settingsRepository ?? appSettingsRepository;
  }

  async validate(apiKey: string): Promise<Pick<SerializedInventoryLocation, 'id' | 'name'>> {
    const axios = await this.resolveAxiosClient();

    if (!axios.defaults.baseURL) {
      throw new Error('API base URL is required for location validation.');
    }

    const validationResponse = await axios.post<ValidateLocationApiKeyResponse>(
      '/inventory-locations/validate-api-key',
      { apiKey },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        validateStatus: () => true,
      },
    );

    if (validationResponse.status < 200 || validationResponse.status >= 300) {
      throw new Error(`Location API key validation failed with HTTP ${validationResponse.status}.`);
    }

    const locationId = validationResponse.data.id;

    const locationAxios = createLocationApiKeyAxiosClient({ axiosClient: axios, locationApiKey: apiKey });
    const locationResponse = await locationAxios.get<{ data: SerializedInventoryLocation }>(
      `/inventory-locations/${locationId}`,
      {
        validateStatus: () => true,
      },
    );

    if (locationResponse.status < 200 || locationResponse.status >= 300) {
      throw new Error(`Location load failed with HTTP ${locationResponse.status}.`);
    }

    return locationResponse.data.data;
  }

  private async resolveAxiosClient() {
    if (this.axiosClient) {
      return this.axiosClient;
    }

    const settings = await this.settingsRepository.load();
    return this.createAxiosClient({ baseUrl: settings.apiBaseUrl });
  }
}
